import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  IntegrationsContext,
  type IntegrationsSettings,
  type Integration,
} from '@/contexts/IntegrationsContext';
import { useFS } from '@/hooks/useFS';
import { readIntegrationsSettings, writeIntegrationsSettings } from '@/lib/configUtils';

const DEFAULT_SETTINGS: IntegrationsSettings = {
  integrations: [],
  projectIntegrations: {},
};

export function IntegrationsProvider({ children }: { children: ReactNode }) {
  const { fs } = useFS();
  const [settings, setSettings] = useState<IntegrationsSettings>(DEFAULT_SETTINGS);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize settings from VFS on mount
  useEffect(() => {
    const initializeSettings = async () => {
      try {
        const settings = await readIntegrationsSettings(fs);
        setSettings(settings);
      } catch (error) {
        console.error('Failed to initialize integrations settings:', error);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeSettings();
  }, [fs]);

  // Save settings to VFS whenever they change (but not during initialization)
  useEffect(() => {
    if (!isInitialized) return;

    const saveSettings = async () => {
      try {
        await writeIntegrationsSettings(fs, settings);
      } catch (error) {
        console.error('Failed to save integrations settings:', error);
      }
    };

    saveSettings();
  }, [fs, settings, isInitialized]);

  const updateSettings = (updates: Partial<IntegrationsSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  const addIntegration = (integration: Integration) => {
    setSettings((prev) => ({
      ...prev,
      integrations: [...prev.integrations, integration],
    }));
  };

  const removeIntegration = (integrationId: string) => {
    setSettings((prev) => ({
      ...prev,
      integrations: prev.integrations.filter((i) => i.id !== integrationId),
      // Remove from all project associations
      projectIntegrations: Object.fromEntries(
        Object.entries(prev.projectIntegrations || {}).map(([projectId, integrationIds]) => [
          projectId,
          integrationIds.filter((id) => id !== integrationId),
        ])
      ),
    }));
  };

  const updateIntegration = (integrationId: string, updates: Partial<Integration>) => {
    setSettings((prev) => ({
      ...prev,
      integrations: prev.integrations.map((integration) =>
        integration.id === integrationId
          ? { ...integration, ...updates, updatedAt: Date.now() }
          : integration
      ),
    }));
  };

  const getIntegration = (integrationId: string): Integration | undefined => {
    return settings.integrations.find((i) => i.id === integrationId);
  };

  const getIntegrationsByType = <T extends Integration>(type: string): T[] => {
    return settings.integrations.filter((i) => i.type === type) as T[];
  };

  const getProjectIntegrations = (projectId: string): Integration[] => {
    const integrationIds = settings.projectIntegrations?.[projectId] || [];
    return integrationIds
      .map((id) => settings.integrations.find((i) => i.id === id))
      .filter((i): i is Integration => i !== undefined);
  };

  const enableIntegrationForProject = (projectId: string, integrationId: string) => {
    setSettings((prev) => {
      const currentIntegrations = prev.projectIntegrations?.[projectId] || [];
      if (currentIntegrations.includes(integrationId)) {
        return prev; // Already enabled
      }

      return {
        ...prev,
        projectIntegrations: {
          ...(prev.projectIntegrations || {}),
          [projectId]: [...currentIntegrations, integrationId],
        },
      };
    });
  };

  const disableIntegrationForProject = (projectId: string, integrationId: string) => {
    setSettings((prev) => {
      const currentIntegrations = prev.projectIntegrations?.[projectId] || [];

      return {
        ...prev,
        projectIntegrations: {
          ...(prev.projectIntegrations || {}),
          [projectId]: currentIntegrations.filter((id) => id !== integrationId),
        },
      };
    });
  };

  const isConfigured = useMemo(() => {
    return settings.integrations.length > 0;
  }, [settings.integrations]);

  const value = {
    settings,
    updateSettings,
    addIntegration,
    removeIntegration,
    updateIntegration,
    getIntegration,
    getIntegrationsByType,
    getProjectIntegrations,
    enableIntegrationForProject,
    disableIntegrationForProject,
    isConfigured,
    isInitialized,
  };

  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
}
